// TV v32 froze the chart's clock.
//
// The countdown-to-bar-close redraws every 500ms and renders
// `barCloseTime - ChartApiInstance.serverTime()`. Up to v26 that clock was
// computed live (`Date.now() + serverTimeOffset`); v32 returns a value CACHED on
// the main thread, which the local datafeed backend only refreshes when it
// handles a REQUEST from the chart (_checkServerTimeUpdate runs at the tail of
// handleRequest). Streaming ticks and bar updates arrive as notifications, not
// requests, so an idle chart stops the clock: the countdown paints once at boot
// and then sits there until a scroll/zoom/symbol switch makes it jump. Market
// status (MarketStatusModel) reads the same clock.
//
// Fix: advance the cached value with the local clock between updates, and
// re-baseline on every value the backend does push, so it ticks without drifting
// off the server. Wrapping the getter (rather than the countdown) also keeps
// market status honest, and the patch is inert on any future library version
// that reports a live time of its own.

const MAX_ATTEMPTS = 10;
const RETRY_MS = 500;

interface PatchableChartApi {
    serverTime: () => number;
    __liveServerTime?: boolean;
}

// `window.ChartApiInstance` lives inside the widget's iframe realm, not ours.
function innerChartApi(container: HTMLElement | null): PatchableChartApi | null {
    const frame = container && container.querySelector("iframe");
    try {
        // A cross-origin contentWindow would throw. The library is served from
        // our own origin (library_path is app-relative), so this is
        // belt-and-braces.
        const api = (frame && (frame.contentWindow as any)?.ChartApiInstance) ?? null;
        return api && typeof api.serverTime === "function" ? api : null;
    } catch (e) {
        return null;
    }
}

function install(container: HTMLElement | null): boolean {
    const api = innerChartApi(container);
    if (!api) return false;
    if (api.__liveServerTime) return true;

    const original = api.serverTime.bind(api);
    // Baseline eagerly: deferring it to the first read would silently drop the
    // wall time between install and that read.
    let baseMs = original();
    let baseAtMs = Date.now();
    api.serverTime = () => {
        const pushed = original();
        if (pushed !== baseMs) {
            baseMs = pushed;
            baseAtMs = Date.now();
        }
        // 0 = the backend has not reported a time yet; nothing to extrapolate from.
        if (!baseMs) return baseMs;
        return baseMs + (Date.now() - baseAtMs);
    };
    api.__liveServerTime = true;
    return true;
}

/**
 * Keeps the chart's server clock ticking. Call once the chart is ready, passing
 * the element the widget was mounted into.
 */
export function keepChartServerTimeLive(container: HTMLElement | null, attempt = 0): void {
    if (install(container)) return;
    // onChartReady is well past ChartApiInstance's creation, but retry a few
    // times rather than lose the countdown outright if that ever stops holding.
    if (attempt < MAX_ATTEMPTS) {
        setTimeout(() => keepChartServerTimeLive(container, attempt + 1), RETRY_MS);
        return;
    }
    // Failing silently here just means the countdown freezes again, so say so.
    console.warn("chart: could not keep server time live — bar-close countdown will not tick");
}
